<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import Spinner from 'shared/components/Spinner.vue';
import AllocationDetailsModal from 'dashboard/components-next/planning/Scheduler/AllocationDetailsModal.vue';
import VerticalAllocationBlock from './VerticalAllocationBlock.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import { getClosedRangesForDay } from 'dashboard/components-next/planning/Scheduler/scheduleUtils';
import { buildAllocationApiPayload } from 'dashboard/components-next/planning/Scheduler/allocationPayload';
import { generateDefaultEventName } from 'dashboard/components-next/planning/Scheduler/generateDefaultEventName';
import { useI18n } from 'vue-i18n';
import { formatTime as formatDisplayTime } from 'shared/helpers/formatTime';
import { getDefaultStageColor } from 'dashboard/routes/dashboard/planning/events/useEventStageColors';

const props = defineProps({
  spaceId: {
    type: [Number, String],
    required: true,
  },
  date: {
    type: Date,
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const isLoading = ref(false);
const spaces = useMapGetter('spaces/getSpaces');
const currentChat = useMapGetter('getSelectedChat');
const gridBody = ref(null);
const ghostAllocation = ref(null);
const pendingAllocation = ref(null);
const allocationDetailsModal = ref(null);
const isDragging = ref(false);
const hasDragged = ref(false);
const dragStartY = ref(0);

// Context menu state
const contextMenuState = ref(null);
const activeSubmenu = ref(null);

// Allocation statuses
const allocationStatuses = [
  'draft',
  'inquiry',
  'confirmed',
  'completed',
  'archived',
];

const getAllocationStatusColor = statusVal => getDefaultStageColor(statusVal);

// Users for context menu manager assignment
const users = computed(() => store.getters['agents/getAgents'] || []);

// Current space and its closed ranges for the day
const currentSpace = computed(() =>
  spaces.value.find(s => s.id === props.spaceId)
);

const closedRangesForDay = computed(() => {
  if (!currentSpace.value) return [];
  return getClosedRangesForDay(
    { schedule: currentSpace.value.schedule },
    props.date
  );
});

const rawAllocations = computed(() => {
  return store.getters['planningAllocations/getAllocations'];
});

// Flatten legacy parent nests; all slots render as independent blocks.
const flattenedAllocations = computed(() => {
  const flattened = [];
  const seen = new Set();

  rawAllocations.value.forEach(allocation => {
    if (!allocation?.id || seen.has(allocation.id)) return;

    if (allocation.sub_allocations && allocation.sub_allocations.length > 0) {
      allocation.sub_allocations.forEach(sub => {
        if (!sub?.id || seen.has(sub.id)) return;
        seen.add(sub.id);
        flattened.push({
          ...sub,
          event_id: sub.event_id || allocation.event_id,
        });
      });
      return;
    }

    seen.add(allocation.id);
    flattened.push(allocation);
  });

  return flattened;
});

const hoverResources = computed(() => {
  if (!currentSpace.value?.resource_id) return [];
  return [
    {
      id: currentSpace.value.resource_id,
      name: currentSpace.value.name,
    },
  ];
});

// Flat independent blocks sorted by start time
const renderItems = computed(() => {
  return [...flattenedAllocations.value]
    .map(alloc => ({
      type: 'allocation',
      allocation: alloc,
      sortTime: new Date(alloc.start_at).getTime(),
    }))
    .sort((a, b) => a.sortTime - b.sortTime);
});

// Fetch allocations
const fetchAllocations = async (showLoading = true) => {
  if (!props.spaceId) return;

  const startAt = new Date(props.date);
  startAt.setHours(0, 0, 0, 0);
  const endAt = new Date(props.date);
  endAt.setHours(23, 59, 59, 999);

  if (showLoading) isLoading.value = true;

  try {
    const space = spaces.value.find(s => s.id === props.spaceId);
    if (space && space.resource_id) {
      await store.dispatch('planningAllocations/fetchGrid', {
        resource_ids: [space.resource_id],
        start_at: startAt.toISOString(),
        end_at: endAt.toISOString(),
      });
    }
  } catch {
    // Failed to fetch allocations
  } finally {
    if (showLoading) isLoading.value = false;
  }
};

watch(
  () => [props.spaceId, props.date],
  () => fetchAllocations(),
  { immediate: true }
);

const hours = Array.from({ length: 24 }, (_, i) => i);

const formatHourLabel = hour => formatDisplayTime(new Date(0, 0, 0, hour));

const getEventStyle = alloc => {
  const start = new Date(alloc.start_at);
  const end = new Date(alloc.end_at);
  const startOfDay = new Date(props.date);
  startOfDay.setHours(0, 0, 0, 0);
  const startMinutes = (start - startOfDay) / 1000 / 60;
  const durationMinutes = (end - start) / 1000 / 60;
  return {
    top: `${Math.max(0, startMinutes)}px`,
    height: `${durationMinutes}px`,
  };
};

// --- Ghost allocation (drag-to-create on empty space) ---
const handleGridMouseDown = event => {
  if (event.target.closest('.vertical-block')) return;

  const rect = gridBody.value.getBoundingClientRect();
  const y = event.clientY - rect.top + gridBody.value.scrollTop;
  const clickMinutes = Math.round(y);

  // Block creation on closed periods
  if (
    closedRangesForDay.value.some(
      r => clickMinutes >= r.startMinutes && clickMinutes < r.endMinutes
    )
  )
    return;

  isDragging.value = true;
  hasDragged.value = false;
  dragStartY.value = y;

  document.addEventListener('mousemove', handleGridMouseMove);
  document.addEventListener('mouseup', handleGridMouseUp);
  event.preventDefault();
};

const handleGridMouseMove = event => {
  if (!isDragging.value) return;
  if (!gridBody.value) return;
  const rect = gridBody.value.getBoundingClientRect();
  const y = event.clientY - rect.top + gridBody.value.scrollTop;
  if (!hasDragged.value && Math.abs(y - dragStartY.value) < 5) return;
  hasDragged.value = true;
  updateGhostFromDrag(dragStartY.value, y);
};

const handleGridMouseUp = () => {
  if (!isDragging.value) return;
  document.removeEventListener('mousemove', handleGridMouseMove);
  document.removeEventListener('mouseup', handleGridMouseUp);
  isDragging.value = false;

  if (!hasDragged.value) {
    ghostAllocation.value = null;
    pendingAllocation.value = null;
    return;
  }

  if (ghostAllocation.value) {
    const space = spaces.value.find(s => s.id === props.spaceId);
    pendingAllocation.value = {
      ...ghostAllocation.value,
      resource: { id: space?.resource_id, name: space?.name },
    };
    nextTick(() => {
      allocationDetailsModal.value?.open();
    });
  }
};

const updateGhostFromDrag = (startY, endY) => {
  const space = spaces.value.find(s => s.id === props.spaceId);
  if (!space || !space.resource_id) return;

  const minY = Math.min(startY, endY);
  const maxY = Math.max(startY, endY);
  const startMinutes = Math.round(minY / 15) * 15;
  const endMinutes = Math.round(maxY / 15) * 15;
  const finalEndMinutes = Math.max(endMinutes, startMinutes + 15);

  const startTime = new Date(props.date);
  startTime.setHours(0, startMinutes, 0, 0);
  const endTime = new Date(props.date);
  endTime.setHours(0, finalEndMinutes, 0, 0);

  ghostAllocation.value = {
    start_at: startTime.toISOString(),
    end_at: endTime.toISOString(),
    resource_id: space.resource_id,
    title: 'New Allocation',
  };
};

const openEvent = eventId => {
  if (!eventId) return;
  const route = {
    name: 'planning_events_show',
    params: { eventId },
  };
  const conversationId = currentChat.value?.id;
  if (conversationId) {
    route.query = { fromConversation: conversationId };
  }
  router.push(route);
};

// --- Allocation block event handlers ---
const handleAllocationClick = ({ allocation }) => {
  openEvent(allocation?.event_id);
};

const handleAllocationUpdate = async (updateData, { refetch = true } = {}) => {
  try {
    const allocation = flattenedAllocations.value.find(
      a => a.id === updateData.id
    );

    // Merge with existing allocation data to preserve title, description, etc.
    const fullUpdateData = {
      ...allocation,
      ...updateData,
    };

    // Optimistically update the store before the API call
    // to prevent the visual snap-back when drag offset resets
    store.commit('planningAllocations/UPDATE_ALLOCATION', fullUpdateData);

    // Strip client-only and read-only fields before sending to the API
    const {
      id,
      _parent,
      is_sub_allocation,
      // eslint-disable-next-line camelcase
      parent_allocation_id: _parentId,
      status: _status,
      sequence: _sequence,
      created_at: _createdAt,
      updated_at: _updatedAt,
      sub_allocations: _subAllocations,
      metadata: _metadata,
      ...apiData
    } = fullUpdateData;

    if (!apiData.event_id && _parent?.event_id) {
      apiData.event_id = _parent.event_id;
    }

    await store.dispatch('planningAllocations/update', { id, ...apiData });
    if (refetch) await fetchAllocations(false);
  } catch (e) {
    // Failed to update — re-fetch to restore correct state
    await fetchAllocations(false);
  }
};

// --- Context menu handlers ---
const handleAllocationContextMenu = ({ allocation, clientX, clientY }) => {
  contextMenuState.value = { allocation, x: clientX, y: clientY };
  activeSubmenu.value = null;
};

const closeContextMenu = () => {
  contextMenuState.value = null;
  activeSubmenu.value = null;
};

const toggleSubmenu = name => {
  activeSubmenu.value = activeSubmenu.value === name ? null : name;
};

const getEventForAllocation = allocation => {
  if (!allocation?.event_id) return null;
  return store.getters['planningEvents/getEventById'](allocation.event_id);
};

const handleContextShowEvent = () => {
  const alloc = contextMenuState.value?.allocation;
  if (alloc?.event_id) {
    closeContextMenu();
    openEvent(alloc.event_id);
  }
};

const handleContextChangeStatus = async statusVal => {
  const alloc = contextMenuState.value?.allocation;
  if (alloc?.event_id) {
    try {
      await store.dispatch('planningEvents/update', {
        id: alloc.event_id,
        system_stage: statusVal,
      });
    } catch {
      // Failed to update status
    }
    closeContextMenu();
  }
};

const handleContextAssignManager = async userId => {
  const alloc = contextMenuState.value?.allocation;
  if (alloc?.event_id) {
    try {
      await store.dispatch('planningEvents/update', {
        id: alloc.event_id,
        event_manager_id: userId,
      });
    } catch {
      // Failed to assign manager
    }
    closeContextMenu();
  }
};

const handleContextCopyLink = async () => {
  const alloc = contextMenuState.value?.allocation;
  if (alloc?.event_id) {
    const url = `${window.location.origin}/app/accounts/${store.getters.getCurrentAccountId}/planning/events/${alloc.event_id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Failed to copy
    }
    closeContextMenu();
  }
};

const handleContextDelete = async () => {
  const alloc = contextMenuState.value?.allocation;
  if (!alloc) return;
  closeContextMenu();
  await deleteAllocation(alloc.id);
};

// --- CRUD ---
const createAllocation = async allocationData => {
  try {
    const contactId =
      allocationData.contact_id || currentChat.value?.meta?.sender?.id;

    const defaultName = generateDefaultEventName({ t });

    await store.dispatch('planningEvents/createWithAllocation', {
      eventData: {
        name: allocationData.title || defaultName,
        description: allocationData.description,
        ...(contactId && { customer_id: contactId }),
      },
      allocationData: buildAllocationApiPayload(allocationData, {
        title: allocationData.title || defaultName,
      }),
    });

    await fetchAllocations(false);
  } catch {
    // Failed to create allocation
  }
};

const updateAllocation = async allocationData => {
  try {
    const { id, ...updateData } = allocationData;
    await store.dispatch('planningAllocations/update', { id, ...updateData });
    await fetchAllocations(false);
  } catch {
    // Failed to update allocation
  }
};

const deleteAllocation = async allocationId => {
  const allocation = flattenedAllocations.value.find(
    a => a.id === allocationId
  );

  try {
    if (allocation?.event_id) {
      try {
        await store.dispatch('planningEvents/delete', allocation.event_id);
      } catch {
        await store.dispatch('planningAllocations/delete', allocationId);
      }
    } else {
      await store.dispatch('planningAllocations/delete', allocationId);
    }
    await fetchAllocations(false);
  } catch {
    // Failed to delete allocation
  }
};

// --- Modal handlers ---
const handleModalConfirm = async allocationData => {
  ghostAllocation.value = null;
  if (allocationData.id) {
    await updateAllocation(allocationData);
  } else {
    await createAllocation(allocationData);
  }
  pendingAllocation.value = null;
};

const handleModalUpdate = allocationData => {
  pendingAllocation.value = {
    ...pendingAllocation.value,
    ...allocationData,
  };
};

const handleModalDelete = async allocationId => {
  pendingAllocation.value = null;
  ghostAllocation.value = null;
  await deleteAllocation(allocationId);
};

const handleModalCancel = () => {
  ghostAllocation.value = null;
  pendingAllocation.value = null;
};

// ESC key to close modal
const handleEscapeKey = event => {
  if (event.key === 'Escape' && pendingAllocation.value) {
    handleModalCancel();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
});

// Load agents when context menu opens
watch(
  () => contextMenuState.value,
  val => {
    if (val && !users.value.length) {
      store.dispatch('agents/get');
    }
  }
);
</script>

<template>
  <div class="flex-1 overflow-hidden relative h-full flex flex-col">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center p-8 flex-1">
      <Spinner />
    </div>

    <!-- Embedded Modal (takes over the full widget) -->
    <AllocationDetailsModal
      v-else-if="pendingAllocation"
      ref="allocationDetailsModal"
      :allocation="pendingAllocation"
      :resource="pendingAllocation.resource"
      :compact="true"
      :embedded="true"
      @confirm="handleModalConfirm"
      @update="handleModalUpdate"
      @delete="handleModalDelete"
      @cancel="handleModalCancel"
    />

    <!-- Scheduler Grid -->
    <div v-else ref="gridBody" class="flex-1 overflow-y-auto relative">
      <div class="relative min-h-[1440px] flex">
        <!-- Time Axis -->
        <div class="w-14 flex-shrink-0 bg-n-background sticky left-0 z-10">
          <div
            v-for="hour in hours"
            :key="hour"
            class="h-[60px] text-xs text-n-slate-10 text-right pr-2 pt-1 relative border-r border-n-weak"
          >
            <span class="-top-3 relative">{{ formatHourLabel(hour) }}</span>
          </div>
        </div>

        <!-- Grid Body -->
        <div
          class="flex-1 relative cursor-crosshair"
          @mousedown="handleGridMouseDown"
        >
          <!-- Grid Lines -->
          <div
            v-for="hour in hours"
            :key="`line-${hour}`"
            class="h-[60px] border-b border-n-weak w-full absolute left-0"
            :style="{ top: `${hour * 60}px` }"
          />

          <!-- Closed period overlays -->
          <div
            v-for="(range, idx) in closedRangesForDay"
            :key="`closed-${idx}`"
            class="absolute left-0 right-0 pointer-events-none bg-n-slate-3/60 bg-[repeating-linear-gradient(-45deg,transparent,transparent_6px,rgba(100,116,139,0.08)_6px,rgba(100,116,139,0.08)_12px)]"
            :style="{
              top: `${range.startMinutes}px`,
              height: `${range.endMinutes - range.startMinutes}px`,
            }"
          />

          <!-- Render flat allocations -->
          <template
            v-for="item in renderItems"
            :key="item.allocation.id"
          >
            <VerticalAllocationBlock
              :allocation="item.allocation"
              :date="date"
              :resources="hoverResources"
              @update="handleAllocationUpdate"
              @click="handleAllocationClick"
              @context-menu="handleAllocationContextMenu"
            />
          </template>

          <!-- Ghost Allocation (drag-to-create) -->
          <div
            v-if="ghostAllocation"
            class="absolute left-1 right-1 rounded-lg border border-dashed border-n-brand/50 bg-white/80 dark:bg-n-solid-2/80 overflow-hidden opacity-80"
            :style="getEventStyle(ghostAllocation)"
          >
            <span
              class="pointer-events-none absolute left-1.5 top-1.5 bottom-1.5 w-1 rounded-full bg-n-brand"
            />
            <div class="flex flex-col justify-center h-full min-w-0 pl-4 pr-2">
              <div class="text-sm font-semibold truncate text-n-brand">
                {{ ghostAllocation.title }}
              </div>
              <div
                class="flex items-center gap-1 text-xs text-n-brand/80 mt-0.5"
              >
                <span class="i-lucide-clock size-3 shrink-0" />
                <span class="truncate">
                  {{ formatDisplayTime(ghostAllocation.start_at) }}
                  -
                  {{ formatDisplayTime(ghostAllocation.end_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Context Menu -->
    <ContextMenu
      v-if="contextMenuState"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      @close="closeContextMenu"
    >
      <div
        class="min-w-[200px] bg-white dark:bg-n-slate-2 rounded-lg shadow-lg border border-n-weak overflow-visible"
      >
        <!-- Show Event -->
        <button
          v-if="contextMenuState.allocation?.event_id"
          type="button"
          class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center gap-3 transition-colors"
          @mousedown.prevent="handleContextShowEvent"
        >
          <span class="i-lucide-external-link size-4 text-n-slate-11" />
          <span>Show event</span>
        </button>

        <!-- Change Status -->
        <div
          v-if="contextMenuState.allocation?.event_id"
          class="relative"
          @mouseleave="activeSubmenu === 'status' && (activeSubmenu = null)"
        >
          <button
            type="button"
            class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center justify-between transition-colors"
            :class="{ 'bg-n-slate-3': activeSubmenu === 'status' }"
            @click.stop="toggleSubmenu('status')"
            @mouseenter="activeSubmenu = 'status'"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-circle-dot size-4 text-n-slate-11" />
              <span>Change status</span>
            </div>
            <span class="i-lucide-chevron-right size-4 text-n-slate-11" />
          </button>
          <div
            v-if="activeSubmenu === 'status'"
            class="absolute top-0 left-full -ml-px bg-white dark:bg-n-slate-2 rounded-lg shadow-lg border border-n-weak min-w-[150px] overflow-hidden z-10"
            @click.stop
          >
            <button
              v-for="s in allocationStatuses"
              :key="s"
              type="button"
              class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 capitalize transition-colors flex items-center gap-2"
              :class="{
                'bg-n-slate-3 font-medium':
                  getEventForAllocation(contextMenuState.allocation)
                    ?.system_stage === s,
              }"
              @mousedown.prevent="handleContextChangeStatus(s)"
            >
              <span
                class="w-3 h-3 rounded-full"
                :style="{ backgroundColor: getAllocationStatusColor(s) }"
              />
              {{ s }}
            </button>
          </div>
        </div>

        <!-- Assign Manager -->
        <div
          v-if="contextMenuState.allocation?.event_id"
          class="relative"
          @mouseleave="activeSubmenu === 'manager' && (activeSubmenu = null)"
        >
          <button
            type="button"
            class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center justify-between transition-colors"
            :class="{ 'bg-n-slate-3': activeSubmenu === 'manager' }"
            @click.stop="toggleSubmenu('manager')"
            @mouseenter="activeSubmenu = 'manager'"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-user-cog size-4 text-n-slate-11" />
              <span>Assign manager</span>
            </div>
            <span class="i-lucide-chevron-right size-4 text-n-slate-11" />
          </button>
          <div
            v-if="activeSubmenu === 'manager'"
            class="absolute top-0 left-full -ml-px bg-white dark:bg-n-slate-2 rounded-lg shadow-lg border border-n-weak min-w-[200px] max-h-[300px] overflow-y-auto z-10"
            @click.stop
          >
            <button
              v-for="user in users"
              :key="user.id"
              type="button"
              class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 transition-colors"
              :class="{
                'bg-n-slate-3 font-medium':
                  getEventForAllocation(contextMenuState.allocation)
                    ?.event_manager_id === user.id,
              }"
              @mousedown.prevent="handleContextAssignManager(user.id)"
            >
              {{ user.name }}
            </button>
          </div>
        </div>

        <!-- Copy Link -->
        <button
          v-if="contextMenuState.allocation?.event_id"
          type="button"
          class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center gap-3 transition-colors border-t border-n-weak"
          @mousedown.prevent="handleContextCopyLink"
        >
          <span class="i-lucide-link size-4 text-n-slate-11" />
          <span>Copy link to event</span>
        </button>

        <!-- Delete -->
        <button
          type="button"
          class="w-full px-4 py-2.5 text-left text-sm text-n-ruby-9 hover:bg-n-ruby-2 flex items-center gap-3 transition-colors border-t border-n-weak"
          @mousedown.prevent="handleContextDelete"
        >
          <span class="i-lucide-trash-2 size-4" />
          <span>Delete</span>
        </button>
      </div>
    </ContextMenu>
  </div>
</template>
